import { useState, useEffect, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@reach/router'
import { ReactComponent as IconWhatsApp } from 'bootstrap-icons/icons/whatsapp.svg'
import helpers from '../../../../helpers'
import useQuery from 'hooks/useQuery'
import useMutation from 'hooks/useMutation'
import useUser from 'hooks/useUser'
import { rejectionReason } from 'utils/arcaRejectionReason'
import { CONDICIONES_IVA } from 'utils/arcaConstants'
import { ArcaStatusIndicator } from 'Components/ArcaStatusIndicator/ArcaStatusIndicator'
import { TrialInfoPopover } from 'Components/TrialInfoPopover/TrialInfoPopover'
import { BetaFeedback } from 'Components/BetaFeedback/BetaFeedback'

const DOC_TIPOS = [
  { id: 99, label: 'Sin identificar (Consumidor Final)' },
  { id: 96, label: 'DNI' },
  { id: 80, label: 'CUIT' },
  { id: 86, label: 'CUIL' },
]

// §6.11 — modalidades de cobro para Responsable Inscripto (determinan el IVA
// discriminado; espejo de fiscal.js del backend, la validación real es ahí)
const MODALIDADES_FISCALES = [
  { id: 'particular', label: 'Particular', tratamiento: 'Gravado 21%' },
  { id: 'obra_social_obligatoria', label: 'Obra social — afiliación obligatoria', tratamiento: 'Exento' },
  { id: 'obra_social_voluntaria', label: 'Obra social — afiliación voluntaria', tratamiento: 'Gravado 10,5%' },
  { id: 'prepaga', label: 'Prepaga (reintegro al paciente)', tratamiento: 'Gravado 21%' },
]
const IVA_RATE = { particular: 0.21, obra_social_voluntaria: 0.105, prepaga: 0.21 }

// Bug real: antes este set traía solo [1, 6] — el subconjunto que el propio
// <select> puede producir a mano — pero `condicionIva` también se precarga
// por sugerencia (historia del paciente/financiador, §6.10) con CUALQUIER
// código válido del backend, incluidos 13 (Monotributista Social) y 16
// (Monotributo Trabajador Independiente Promovido), que NO aparecen en el
// dropdown pero SÍ disparan Factura A en fiscal.js#resolveCbteTipo (son
// variantes de Monotributo, igual que el 6 que sí estaba cubierto). Con el
// set viejo, ese caso pasaba el chequeo client-side sin exigir CUIT y recién
// se enteraba al rechazo del backend (RECEPTOR_CUIT_REQUERIDO_FACTURA_A, ya
// traducido, pero un viaje evitable). Ahora espeja EXACTAMENTE
// CONDICIONES_RECEPTOR_FACTURA_A de fiscal.js, no un subconjunto.
const FACTURA_A_CONDICIONES = [1, 6, 13, 16]

// Sin un label acá, useMutation#request muestra el CÓDIGO crudo del backend
// tal cual (throw Error(data.error), nunca traducido) — cubre TODOS los
// EmitError de emit.js#createInvoice/createCreditNote, no solo los que ya se
// habían mapeado, para que ningún rechazo real quede mostrando un código en
// vez de una explicación.
const ERROR_LABEL = {
  NO_FISCAL_IDENTITY: 'El profesional no tiene la vinculación ARCA configurada',
  // "identidad fiscal" era el único lugar de este archivo donde aparecía
  // ese término (hallazgo de copy, pasada 7 del loop) — en el resto de la
  // pantalla el mismo concepto siempre se llama CUIT (el selector dice
  // "Facturar con el CUIT de") o vinculación ARCA.
  IDENTITY_NOT_AVAILABLE: 'El CUIT que habías elegido ya no está disponible — cerrá y volvé a abrir el modal',
  EMIT_NOT_ALLOWED: 'No estás autorizado a emitir facturas con ese CUIT',
  LINK_NOT_ACTIVE: 'La vinculación ARCA no está activa',
  INVALID_CUIT: 'La vinculación tiene un CUIT inválido — contactá a soporte',
  NO_PUNTO_VENTA: 'No hay puntos de venta Web Services habilitados',
  PUNTO_VENTA_INVALIDO: 'El punto de venta elegido no es válido',
  INVALID_AMOUNT: 'El turno no tiene un monto válido para facturar',
  RECEPTOR_REQUIRED: 'Falta el nombre del receptor de la factura',
  RECEPTOR_DOC_REQUIRED: 'Falta el número de documento del receptor',
  RECEPTOR_DOC_INVALID: 'El documento del receptor es inválido',
  CREDIT_NOTE_EXISTS: 'La factura ya tiene una Nota de Crédito',
  INVOICE_NOT_APPROVED: 'Solo se puede anular una factura aprobada',
  INVOICE_EXISTS: 'El turno ya tiene una factura emitida o en curso — actualizá la vista',
  INVALID_SERVICE_PERIOD: 'Las fechas del servicio son inválidas',
  INVALID_DESCRIPCION: 'La descripción es demasiado larga (máx. 300 caracteres)',
  INVALID_CONDICION_IVA: 'La condición IVA del receptor es inválida',
  MODALIDAD_FISCAL_REQUIRED: 'Como Responsable Inscripto, tenés que indicar la modalidad de cobro',
  RECEPTOR_CUIT_REQUERIDO_FACTURA_A: 'Factura A requiere el CUIT del receptor (no DNI ni consumidor final)',
  EVENT_NOT_FOUND: 'No se encontró el turno a facturar — actualizá la vista',
  EVENT_WITHOUT_RESOURCE: 'El turno no tiene un profesional asignado',
  // Los 3 de abajo son de createCreditNote (emit.js) — cruzando cada código
  // real contra este mapeo (mismo ángulo que ya encontró gaps en ArcaGrants.js/
  // Arca.js) faltaban estos 3, específicos del flujo de anular con NC.
  ALREADY_CREDIT_NOTE: 'El comprobante ya es una Nota de Crédito — no se puede anular de nuevo',
  INVOICE_NOT_FOUND: 'La factura no existe o no pertenece a este equipo — actualizá la vista',
  UNSUPPORTED_TIPO: 'No se puede generar una Nota de Crédito para este tipo de comprobante — contactá a soporte',
  // Bug real: requireArcaEnabled (router.js) corre ANTES del try/catch que
  // traduce los EmitError — si el team pierde el feature flag ARCA (vía
  // Igor) mientras el profesional tiene este modal abierto (enabledQuery
  // cachea el flag hasta 30s, staleTime), el submit puede llegar a pegar
  // contra el backend con el flag YA apagado y volver un 404 crudo
  // `{error:'not_found'}` — sin este mapeo, el profesional veía literalmente
  // el string en inglés "not_found" en vez de una explicación.
  not_found: 'Esta funcionalidad ya no está disponible para tu equipo — actualizá la página',
}

const formatAmount = value => Number(value).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

// Bug real (grave): el monto es editable a mano (§3.8) y este campo es texto
// libre (inputMode="decimal" no restringe qué se puede tipear) — la primera
// versión de este parser solo reemplazaba UNA coma por un punto, sin
// considerar el separador de miles. "15.000" (forma NATURAL de escribir "15
// mil pesos" sin centavos) parseaba SILENCIOSAMENTE como 15 (Number("15.000")
// es una fracción válida en JS) — un profesional corrigiendo el monto de un
// turno podía terminar emitiendo una factura real, con CAE irreversible, por
// 1/1000 del monto real, sin ningún error.
//
// Ese primer fix (detectar coma → asumir formato AR: puntos=miles,
// coma=decimal) seguía siendo vulnerable al mismo bug si alguien tipeaba en
// formato US (coma=miles, punto=decimal) — nada en el input lo impide, y es
// plausible copiando un monto de un Excel/sistema en inglés: "15,000.50" →
// con la lógica vieja, ve la coma, borra TODOS los puntos (asumiendo AR) →
// "15,00050" → coma a punto → "15.00050" → 15.0005 (¡se esperaba 15000.50!).
//
// Regla robusta (sin asumir el idioma de quien tipeó): si hay COMA y PUNTO a
// la vez, el que está más a la DERECHA es el separador decimal (convención
// universal en cualquier idioma) y el otro es de miles, se descarta. Si hay
// un solo tipo de separador, solo se lo trata como agrupador de miles cuando
// el string entero calza con el patrón estricto de grupos de EXACTAMENTE 3
// dígitos (`"15.000"`, `"15,000"`, `"1.234.567"`) — nunca 3 decimales, algo
// que no ocurre en montos de dinero (siempre 2) — si no, es el separador
// decimal (el caso común de este campo, en cualquiera de las dos convenciones).
const GROUP3_DOT_RE = /^\d{1,3}(\.\d{3})+$/
const GROUP3_COMMA_RE = /^\d{1,3}(,\d{3})+$/
const parseAmountInput = raw => {
  const str = String(raw ?? '').trim()
  const lastComma = str.lastIndexOf(',')
  const lastDot = str.lastIndexOf('.')
  if (lastComma !== -1 && lastDot !== -1) {
    return lastComma > lastDot
      ? Number(str.replace(/\./g, '').replace(',', '.')) // AR: puntos=miles, coma=decimal
      : Number(str.replace(/,/g, '')) // US: comas=miles, punto=decimal (ya queda parseable tal cual)
  }
  if (lastComma !== -1) return Number(GROUP3_COMMA_RE.test(str) ? str.replace(/,/g, '') : str.replace(',', '.'))
  if (GROUP3_DOT_RE.test(str)) return Number(str.replace(/\./g, ''))
  return Number(str)
}

// Fecha ARGENTINA en formato AAAA-MM-DD (el que espera <input type="date">) —
// mismo criterio de huso horario que emit.js#toArgDate en el backend (evita
// que un turno a la noche ART aparezca "un día después" por UTC).
const toArgDateInput = epochMs =>
  new Intl.DateTimeFormat('fr-CA', { timeZone: 'America/Argentina/Buenos_Aires' }).format(new Date(Number(epochMs)))

// AAAA-MM-DD (el formato de <input type="date">) → D/M/AAAA, mismo formato
// que ya usa el template de descripción para la fecha del turno (es-AR sin
// ceros a la izquierda) — anclado a mediodía ART para no arriesgar un
// corrimiento de día por huso horario en la conversión.
const formatArgDateDisplay = isoDate =>
  new Date(`${isoDate}T12:00:00-03:00`).toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' })

// AAAAMMDD (lo que guarda/devuelve el backend) → AAAA-MM-DD (<input type="date">)
const isoFromArgDate = yyyymmdd => `${yyyymmdd.slice(0, 4)}-${yyyymmdd.slice(4, 6)}-${yyyymmdd.slice(6, 8)}`

// Botón "Facturar" del turno + modal de emisión (plan §5.2). Solo se muestra
// cuando el turno está cobrado (misma condición que "Pagado" en Event.js).
// Bug real (UX, pedido de Nacho, 2026-08-05): el aviso post-emisión decía
// "Si el paciente tiene email cargado, se le envía automáticamente..." — un
// hedge innecesario, porque esto SÍ se puede chequear (mismo dato que
// `phones`, ya derivado de event.consumer más abajo). Mostrar el email real
// (o afirmar con seguridad que no hay ninguno) es más preciso que una
// condicional vaga que el profesional no puede verificar desde la UI.
// Bug real (2026-08-05, encontrado probando en QA): el consumer embebido en
// un turno lleva el email en el campo SINGULAR `email` (string) — el mismo
// que ya usa el propio backend para las notificaciones reales del turno
// (api/src/events/notify.js#`event.consumer.email`) — no en `emails`
// (array), que es el shape del documento COMPLETO del consumer (Consumers/
// Form/Form.js). Chequear solo `emails` significaba que prácticamente
// SIEMPRE decía "no tiene email cargado", aunque el paciente sí tuviera uno.
const consumerEmail = consumer => {
  const emails = [
    ...(consumer?.email ? [consumer.email] : []),
    ...(consumer?.emails || []).map(item => (typeof item === 'string' ? item : item?.value)),
  ]
    .map(helpers.getEmail)
    .filter(helpers.isEmail)
  return emails[0] || null
}

export function Facturar({ event, phones = [] }) {
  const { t } = useTranslation()
  const user = useUser()
  const [open, setOpen] = useState(false)
  // 'form' | 'confirm' | 'polling' | 'done' | 'failed'
  const [step, setStep] = useState('form')
  const [error, setError] = useState(null)
  const [invoice, setInvoice] = useState(null)
  const [receptor, setReceptor] = useState(null)
  const [condicionIva, setCondicionIva] = useState(5)
  const [descripcion, setDescripcion] = useState('')
  const [servDesde, setServDesde] = useState('')
  const [servHasta, setServHasta] = useState('')
  const [modalidadFiscal, setModalidadFiscal] = useState('particular')
  const [amount, setAmount] = useState('')
  const [ncBusy, setNcBusy] = useState(false)
  const [showTips, setShowTips] = useState(false)
  const [identityId, setIdentityId] = useState(null)
  const pollStartedAt = useRef(null)
  const feedbackRef = useRef(null)
  const modalRef = useRef(null)

  // closeModal se define ACÁ (no junto a openModal, más abajo) porque el
  // efecto de a11y que sigue lo necesita, y todo hook debe llamarse ANTES de
  // cualquier `return null` condicional (los 3 early-return de más abajo) —
  // rules-of-hooks. openModal en cambio no lo necesita ningún hook, así que
  // se queda donde estaba.
  const closeModal = () => {
    pollStartedAt.current = null
    setStep('form') // corta el poll (enabled depende de step) — la emisión sigue en background
    setOpen(false)
  }

  // A11y del modal (pasada 5 del loop): faltaba respecto del patrón ya
  // establecido en Odontograma.js/Percentiles.js del mismo sprint — foco
  // inicial, ESC para cerrar, aria-modal/aria-labelledby (estos 2 últimos en
  // el JSX, más abajo). Justo el tipo de acción (emitir un comprobante
  // fiscal, irreversible) donde un usuario de teclado/lector de pantalla más
  // necesita esa claridad. Deps `[open]`, no vacías: a diferencia de
  // Percentiles.js (que desacopla con un ref porque `onClose` le llega como
  // PROP inestable de un padre con polling), acá `open` es estado local
  // booleano — solo cambia por las propias openModal/closeModal, así que el
  // efecto corre una vez por apertura real, sin robarle el foco al usuario
  // en renders ajenos.
  useEffect(() => {
    if (!open) return
    // Restaurar el foco al trigger al cerrar (hallazgo real verificado en
    // vivo contra QA, pasada 7 del loop: con ESC el foco caía en <body>).
    // document.activeElement en este punto es el botón "Facturar" que
    // disparó la apertura — se guarda ANTES de robarle el foco al modal.
    const previouslyFocused = document.activeElement
    modalRef.current?.focus()
    const onKey = event_ => { if (event_.key === 'Escape') closeModal() }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const resourceId = event.resource?.id
  // Feature flag por team (habilitado desde Igor, apagado por default) — a
  // diferencia de las queries de abajo (gateadas por `open`, recién se
  // necesitan al abrir el modal), esta corre siempre: determina si se
  // muestra el botón "Facturar" en sí. Falla CERRADO: mientras carga o si
  // falla, no se muestra nada — el resto de los teams no debería ni saber
  // que esto existe.
  const enabledQuery = useQuery(window._l('/arca/enabled').substr(1), { enabled: !!resourceId, staleTime: 30000 })
  const arcaEnabled = !!enabledQuery.data?.enabled
  // Sin `open` (reporte de Nacho, 2026-08-21): el link "Facturar" aparecía
  // en turnos de profesionales SIN ninguna identidad ARCA (propia ni
  // delegada) y recién al abrir el modal se enteraba de que no podía emitir.
  // Con el prefetch, el link se oculta directamente en esos turnos.
  // staleTime ALTO a propósito: <Event> se monta por fila de la agenda, así
  // que esta query corre por cada profesional visible — 5 min de cache evita
  // repetirla al scrollear/cambiar de día (las identidades cambian poco).
  const identitiesQuery = useQuery(window._l(`/arca/${resourceId}/identities`).substr(1), { enabled: !!resourceId && arcaEnabled, staleTime: 300000 })
  const invoicesQuery = useQuery(window._l(`/arca/${event.id}/invoices`).substr(1), { enabled: open && arcaEnabled })
  const healthQuery = useQuery(window._l('/arca/health').substr(1), { enabled: open && arcaEnabled, staleTime: 30000 })
  // Sugerencia de precarga (§6.10): última factura del paciente → del
  // financiador → default del profesional → Consumidor Final
  const suggestionQuery = useQuery(window._l(`/arca/${event.id}/suggestion`).substr(1), { enabled: open && arcaEnabled })
  const suggestionApplied = useRef(false)
  // Si el usuario ya empezó a editar receptor/condición, una sugerencia que
  // llega tarde (red lenta) NO debe pisarle lo tipeado.
  const formTouched = useRef(false)
  // Independiente de formTouched: solo trackea si el usuario reescribió el
  // detalle a mano. Mientras no lo haya hecho, cambiar el rango de fechas
  // regenera el texto del detalle para que coincida — sin esto, extender el
  // período a varias sesiones dejaba el detalle pisado con el template de un
  // solo día, la MISMA inconsistencia (línea estructurada vs. texto libre en
  // el PDF) que este período fue agregado para resolver.
  const descripcionTouched = useRef(false)

  const applyServicePeriodToDescripcion = (desde, hasta) => {
    if (descripcionTouched.current) return
    if (!desde) { setDescripcion(''); return }
    const desdeDisplay = formatArgDateDisplay(desde)
    setDescripcion(
      hasta && hasta !== desde
        ? `Sesiones de consulta — período ${desdeDisplay} al ${formatArgDateDisplay(hasta)}`
        : `Sesión de consulta — ${desdeDisplay}`
    )
  }

  useEffect(() => {
    if (!open || suggestionApplied.current || formTouched.current) return
    const suggestion = suggestionQuery.data?.suggestion
    if (!suggestion) return
    suggestionApplied.current = true
    // Bug real (UX, 2026-08-05): la sugerencia de consumer_history es un
    // SNAPSHOT de la última factura de este paciente — si esa factura se
    // emitió como "Sin identificar" (antes de que existiera el
    // autocompletado de arriba, o por error), esta llegaba tarde (es async)
    // y pisaba el DNI que openModal() ya había precargado, dejando "Sin
    // identificar" — y como esa MISMA factura nueva pasa a ser la última del
    // paciente, el problema se autoperpetuaba en cada factura siguiente. El
    // DNI vigente en la ficha del paciente es el dato más actualizado — no
    // debe retroceder a "Sin identificar" solo porque la historia lo dice.
    const consumerDni = event.consumer?.identification || ''
    const suggestsSinIdentificar = Number(suggestion.receptor?.docTipo) === 99
    if (suggestion.receptor && !(consumerDni && suggestsSinIdentificar)) {
      setReceptor({
        label: suggestion.receptor.label,
        docTipo: suggestion.receptor.docTipo,
        docNro: suggestion.receptor.docNro === '0' ? '' : suggestion.receptor.docNro,
      })
    }
    setCondicionIva(suggestion.condicionIvaReceptorId)
    if (suggestion.modalidadFiscal) setModalidadFiscal(suggestion.modalidadFiscal)
  }, [open, suggestionQuery.data]) // eslint-disable-line react-hooks/exhaustive-deps

  // Factura más reciente de ESTE turno que NO sea una Nota de Crédito —
  // compartida entre el efecto de abajo (receptor/condición/período/detalle/
  // monto) y el que restaura la identidad fiscal (más abajo). Antes cada uno
  // recalculaba el mismo filtro por separado — mismo riesgo que ya se vio
  // esta sesión con formTouched: un cambio futuro a uno de los dos filtros
  // podía divergir del otro en silencio, sin que nada lo marque.
  const previousInvoice = useMemo(() => {
    const rows = invoicesQuery.data?.invoices
    if (!rows) return null
    // Excluye las propias Notas de Crédito: heredan el detalle del original
    // con el prefijo "Anulación de: " (emit.js#createCreditNote) — arrancar
    // la factura corregida con ESE texto (en vez del original limpio) sería
    // un error nuevo, no una corrección.
    const candidates = rows.filter(row => !row.creditNoteOfId)
    return candidates.length ? [...candidates].sort((a, b) => b.createdAt - a.createdAt)[0] : null
  }, [invoicesQuery.data])

  // Corregir y re-emitir tras una Nota de Crédito (o un rechazo, si el
  // profesional cerró el modal antes de reintentar): openModal() siempre
  // resetea a los defaults del turno — sin esto, el receptor/período/detalle
  // que el profesional ya había cargado en la factura anterior se perdía por
  // completo al reabrir, obligando a recargar todo desde cero. Más específico
  // que la sugerencia general de arriba (que mira la historia del paciente en
  // OTROS turnos) — si hay una factura previa de ESTE turno, gana ella.
  const invoiceDataApplied = useRef(false)
  useEffect(() => {
    if (!open || invoiceDataApplied.current || formTouched.current) return
    const previous = previousInvoice
    if (!previous) return
    invoiceDataApplied.current = true
    suggestionApplied.current = true // más específico — que no la pise la sugerencia general si llega después
    if (previous.receptor?.label) {
      setReceptor({
        label: previous.receptor.label,
        docTipo: previous.receptor.docTipo ?? 99,
        docNro: previous.receptor.docNro === '0' ? '' : previous.receptor.docNro,
      })
    }
    if (previous.condicionIvaReceptorId) setCondicionIva(previous.condicionIvaReceptorId)
    if (previous.modalidadFiscal) setModalidadFiscal(previous.modalidadFiscal)
    if (previous.impTotal) setAmount(String(previous.impTotal))
    if (previous.servDesde) {
      setServDesde(isoFromArgDate(previous.servDesde))
      setServHasta(isoFromArgDate(previous.servHasta || previous.servDesde))
    }
    if (previous.descripcion) { descripcionTouched.current = true; setDescripcion(previous.descripcion) }
  }, [open, previousInvoice]) // eslint-disable-line react-hooks/exhaustive-deps

  const suggestionSource = suggestionQuery.data?.suggestion?.source

  // Poll del estado mientras la emisión está en cola — via useQuery para
  // reusar la autenticación Auth0 del resto de la app
  const pollQuery = useQuery(window._l(`/arca/invoices/${invoice?.id}`).substr(1), {
    enabled: step === 'polling' && !!invoice?.id,
    refetchInterval: step === 'polling' ? 2500 : false,
  })

  const [mutateEmit] = useMutation(window._l('/arca/invoices').substr(1), {
    onSuccess: payload => {
      if (payload?.invoice?.id) {
        setInvoice(payload.invoice)
        setStep('polling')
      } else {
        setError(ERROR_LABEL[payload?.error] || payload?.message || 'Error inesperado')
        setStep('form')
      }
    },
    onError: mutationError => {
      setError(ERROR_LABEL[mutationError?.message] || mutationError?.message || 'Error inesperado')
      setStep('form')
    },
  })

  const identities = (identitiesQuery.data?.identities || []).filter(identity => identity.canEmit)
  const identity = identities.find(item => item.fiscalResourceId === identityId) || identities[0]
  const esResponsableInscripto = identity?.condicionFiscal === 'responsable_inscripto'

  const chain = invoicesQuery.data?.invoices || []
  // Más reciente primero (orden del backend): tras una re-emisión corregida,
  // `approved` es la factura nueva, no la anulada.
  const approved = chain.find(row => row.status === 'approved' && !row.creditNoteOfId)
  const activeNC = approved && chain.find(row =>
    String(row.creditNoteOfId) === String(approved.id) && ['queued', 'processing', 'approved'].includes(row.status)
  )
  const inFlight = chain.find(row => ['queued', 'processing'].includes(row.status))
  // Se puede emitir de nuevo si no hay nada en vuelo y la última aprobada quedó
  // anulada (re-emisión corregida, §3.8) — o si nunca se facturó.
  const canEmitNew = !inFlight && (!approved || !!activeNC)

  // Restaura la identidad fiscal (CUIT) bajo la que salió la factura anterior
  // de este turno — separado del efecto de arriba (invoiceDataApplied) porque
  // depende de OTRA query (identitiesQuery) que puede resolver en un orden
  // distinto. Sin esto, "corregir y reintentar" (o reabrir tras un rechazo)
  // restauraba receptor/condición/período/detalle de la factura original pero
  // el selector de identidad volvía al default (la propia) — si la factura
  // original salió bajo una identidad DELEGADA, la corrección podía terminar
  // re-emitiéndose bajo un CUIT distinto al que en realidad se está
  // corrigiendo. No marca "aplicado" hasta tener AMBAS queries resueltas, para
  // no perderse si invoicesQuery resuelve primero.
  const identityRestored = useRef(false)
  useEffect(() => {
    if (!open || identityRestored.current || formTouched.current) return
    if (!invoicesQuery.data || !identitiesQuery.data) return
    if (!previousInvoice?.cuitEmisor) return
    identityRestored.current = true
    const matched = identities.find(candidate => candidate.cuit === previousInvoice.cuitEmisor)
    if (matched) setIdentityId(matched.fiscalResourceId)
  }, [open, invoicesQuery.data, identitiesQuery.data, previousInvoice]) // eslint-disable-line react-hooks/exhaustive-deps

  // Reaccionar al resultado del poll: terminado → done/failed; +30s en cola →
  // dejar de esperar en vivo (la emisión sigue en background, plan §3.11)
  //
  // Bug real: la dependencia original era solo `pollQuery.data` — si el poll
  // arranca a fallar (network blip, o el team pierde el feature flag ARCA a
  // mitad de camino vía Igor: requireArcaEnabled devuelve 404 para GET
  // /arca/invoices/:id igual que para el resto de las rutas) `data` deja de
  // cambiar de referencia (react-query retiene el último valor bueno — o
  // `undefined` si nunca hubo ninguno — en un refetch fallido), así que este
  // efecto directamente DEJA DE CORRER y el chequeo de 30s nunca se vuelve a
  // evaluar: el profesional queda mirando "Emitiendo..." para siempre, exacto
  // el escenario que este efecto existe para evitar, solo que por la vía del
  // error en vez de la vía de "sigue en cola". `dataUpdatedAt`/
  // `errorUpdatedAt` sí cambian en CADA intento del poll, éxito o error.
  useEffect(() => {
    if (step !== 'polling') return
    if (!pollStartedAt.current) pollStartedAt.current = Date.now()

    const current = pollQuery.data?.invoice
    if (current) {
      setInvoice(current)
      if (current.status === 'rejected' || current.status === 'failed') {
        pollStartedAt.current = null
        setStep('failed')
        invoicesQuery.refetch()
        return
      }
      // Bug real: emitir (job 'emit', pone status='approved' + CAE) y generar
      // el PDF (job 'deliver', SEPARADO y posterior en la cola — tasks.js)
      // no son atómicos. Cortar el polling apenas 'approved' (sin esperar al
      // pdf) dejaba al profesional viendo el CAE pero NUNCA el link — el
      // polling ya se había detenido, así que un 'deliver' que tardara un
      // poco más nunca se reflejaba en la UI salvo cerrando y reabriendo el
      // modal a mano.
      if (current.status === 'approved' && current.pdf) {
        pollStartedAt.current = null
        setStep('done')
        invoicesQuery.refetch()
        return
      }
    }
    if (Date.now() - pollStartedAt.current > 30000) {
      pollStartedAt.current = null
      setStep('queued-long')
    }
  }, [step, pollQuery.data, pollQuery.dataUpdatedAt, pollQuery.errorUpdatedAt]) // eslint-disable-line react-hooks/exhaustive-deps

  const [mutateNC] = useMutation(
    window._l(`/arca/invoices/${approved?.id || invoice?.id || 0}/credit-note`).substr(1),
    {
      onSuccess: payload => {
        setNcBusy(false)
        if (payload?.invoice?.id) {
          setInvoice(payload.invoice)
          setStep('polling')
        } else {
          // Bug real: mismo escenario defensivo que ya cubre mutateEmit (2xx
          // con payload malformado/vacío, sin invoice.id) — sin este else, acá
          // no pasaba NADA: ncBusy volvía a false, error ya se había limpiado
          // en emitNC() antes de disparar, así que el profesional se quedaba
          // sin ningún indicio de que "Anular" no hizo efecto (ni error, ni
          // cambio de estado), en vez de la explicación que mutateEmit sí da.
          setError(ERROR_LABEL[payload?.error] || payload?.message || 'Error inesperado')
        }
      },
      onError: mutationError => {
        setNcBusy(false)
        setError(ERROR_LABEL[mutationError?.message] || mutationError?.message || 'Error inesperado')
      },
    }
  )

  // Antes exigía event.pricing?.total para mostrarse — pero un turno creado
  // a mano en la agenda nunca lo tiene (las Reglas de cobro son para turnos
  // que vienen de Crontu, no alimentan la creación manual), así que esto
  // bloqueaba facturar cualquier turno sin ese origen externo. El monto ya
  // es 100% editable en el form de abajo (§3.8) — no hace falta un precio
  // precargado para que la validación real (>0 al momento de emitir) tenga
  // sentido.
  if (!resourceId) return null
  if (!arcaEnabled) return null
  // Reporte de Nacho (2026-08-21): el link "Facturar" aparecía en turnos de
  // profesionales sin NINGUNA identidad ARCA (ni propia ni delegada) — el
  // usuario logueado tenía ARCA, pero el turno era de un colega sin vincular,
  // y recién al abrir el modal aparecía el aviso. Sin identidades no hay nada
  // que este componente pueda hacer, así que no se muestra nada. Excepciones
  // deliberadas: (a) si hay una factura ya emitida sobre el turno
  // (event.invoice, lo estampa el worker server-side) hay que poder verla;
  // (b) si el modal ya está abierto, ocultarlo dejaría la UI colgada;
  // (c) mientras la query carga o si falla, se mantiene el comportamiento
  // previo (link visible — el modal explica el detalle con el nombre).
  // Condición hoisteada a consts simples (no inline con optional-chaining):
  // mismo false-positive de babel-eslint ya documentado más abajo en este
  // archivo (invoiceApproved) — la expresión compleja como condición hace
  // que marque hooks previos como "llamados condicionalmente".
  const identitiesResolved = !!identitiesQuery.data
  const noEmittableIdentity = identitiesResolved && identities.length === 0
  if (!open && !event.invoice && noEmittableIdentity) return null

  const openModal = () => {
    // Bug real (pasada 5 del loop): un intento de emisión puede fallar SIN
    // persistir ninguna factura (red caída, o el team pierde el flag ARCA a
    // mitad de camino — requireArcaEnabled corre antes del try/catch, ver
    // ERROR_LABEL.not_found más abajo) — para ese caso no existe ningún
    // `previousInvoice` que restaure lo tipeado. Si el profesional cierra y
    // reabre el modal después de un rechazo así, resetear incondicionalmente
    // tiraba a la basura identidad/receptor/período/detalle/monto que ya
    // había cargado, sin ningún aviso. formTouched ya distingue "usuario
    // editó algo en este intento" (se resetea a false más abajo SOLO en el
    // camino de reset real) — si sigue en true, hubo un intento sin éxito:
    // se preserva tal cual quedó, solo se limpian error/step/invoice.
    if (formTouched.current) {
      setError(null)
      setInvoice(null)
      setStep('form')
      setOpen(true)
      return
    }
    // Si el paciente ya tiene DNI cargado en su ficha, precargarlo (evita
    // retipearlo a mano) — puede quedar pisado más abajo por la sugerencia
    // de la última factura o por la factura previa del turno, mismo orden
    // que ya existía para label/condicionIva.
    const consumerDni = event.consumer?.identification || ''
    setReceptor({
      label: event.consumer?.label || '',
      docTipo: consumerDni ? 96 : 99,
      docNro: consumerDni,
    })
    setCondicionIva(5)
    // Período del servicio (§6.9, reintegros): default = la fecha del turno
    // en ambos extremos. Editable para extender el rango cuando se factura
    // más de una sesión junta — antes solo se podía "simularlo" escribiéndolo
    // en el detalle, sin que la fecha estructurada del comprobante (la que
    // lee el QR/PDF) reflejara el rango real.
    const eventDateInput = event.startsAt ? toArgDateInput(event.startsAt) : ''
    setServDesde(eventDateInput)
    setServHasta(eventDateInput)
    setAmount(String(event.pricing?.total ?? ''))
    setModalidadFiscal('particular')
    setIdentityId(null)
    setError(null)
    setInvoice(null)
    setStep('form')
    suggestionApplied.current = false // re-aplicar la sugerencia en cada apertura
    invoiceDataApplied.current = false // ídem: re-chequear si hay una factura previa de este turno
    identityRestored.current = false // ídem: re-chequear la identidad de la factura previa
    formTouched.current = false
    descripcionTouched.current = false
    // Template de detalle pensado para reintegros (§6.9): qué se facturó y
    // cuándo — mismo helper que sincroniza el detalle cuando se edita el
    // período más abajo, para no duplicar el formateo de fecha.
    applyServicePeriodToDescripcion(eventDateInput, eventDateInput)
    setOpen(true)
  }

  const emit = () => {
    // Bug real: nada impedía vaciar el campo "Factura a nombre de" y emitir —
    // el backend lo rechaza (RECEPTOR_REQUIRED), pero ese código no estaba en
    // ERROR_LABEL, así que el usuario veía el código crudo del backend como
    // mensaje de error (useMutation#request tira Error(data.error) tal cual,
    // sin traducir). Se valida acá para no hacer ni siquiera el viaje.
    if (!receptor.label.trim()) {
      setError(t('arca_receptor_required', 'Falta el nombre del receptor de la factura'))
      return
    }
    // Mismo bug que arriba, para el documento: docTipo!=99 (DNI/CUIT/CUIL)
    // con el número vacío pasaba de largo hasta el rechazo del backend
    // (RECEPTOR_DOC_REQUIRED — sí traducido, pero era un viaje evitable).
    const docTipoNum = Number(receptor.docTipo)
    const docNroDigits = String(receptor.docNro || '').replace(/\D/g, '')
    if (docTipoNum !== 99 && !docNroDigits) {
      setError(t('arca_receptor_doc_required', 'Falta el número de documento del receptor'))
      return
    }
    // CUIT (80) y CUIL (86) son 11 dígitos (misma validación que
    // emit.js#validateReceptor) — un typo (menos/más dígitos) también se
    // puede cortar acá antes de intentar emitir.
    if ((docTipoNum === 80 || docTipoNum === 86) && docNroDigits.length !== 11) {
      setError(t('arca_receptor_doc_invalid_length', `El ${docTipoNum === 80 ? 'CUIT' : 'CUIL'} del receptor debe tener 11 dígitos`))
      return
    }
    const parsedAmount = parseAmountInput(amount)
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      setError(t('arca_amount_invalid', 'El monto a facturar debe ser mayor a cero'))
      return
    }
    if (esResponsableInscripto && FACTURA_A_CONDICIONES.includes(Number(condicionIva)) && Number(receptor.docTipo) !== 80) {
      setError(t('arca_receptor_cuit_requerido', 'Para facturar a un Responsable Inscripto/Monotributista (Factura A) necesitás el CUIT del receptor — cambiá el documento a CUIT o elegí otra condición IVA'))
      return
    }
    if (servDesde && servHasta && servHasta < servDesde) {
      setError(t('arca_periodo_invalido', 'La fecha de fin del servicio no puede ser anterior a la de inicio'))
      return
    }
    setError(null)
    setStep('polling-start')
    mutateEmit({
      eventId: event.id,
      amount: parsedAmount,
      fiscalResourceId: identity?.fiscalResourceId,
      receptor: {
        label: receptor.label,
        docTipo: Number(receptor.docTipo),
        docNro: receptor.docNro,
      },
      condicionIvaReceptorId: Number(condicionIva),
      descripcion: descripcion.trim() || undefined,
      // AAAAMMDD (backend) — <input type="date"> ya entrega AAAA-MM-DD, solo hay que sacar los guiones.
      servDesde: servDesde ? servDesde.replace(/-/g, '') : undefined,
      servHasta: servHasta ? servHasta.replace(/-/g, '') : undefined,
      modalidadFiscal: esResponsableInscripto ? modalidadFiscal : undefined,
    })
  }

  const emitNC = () => {
    if (ncBusy) return
    if (!window.confirm(t('arca_nc_confirm', 'Se va a emitir una Nota de Crédito por el total de la factura. Esta acción no se puede deshacer. ¿Continuar?'))) return
    setError(null)
    setNcBusy(true)
    mutateNC({})
  }

  const waLink = (pdf, isCreditNote = false) => {
    try {
      const phone = phones.at(0)
      if (!phone?.value || !pdf) return null
      const number = phone.value.replace(/\D/g, '')
      // Mismo umbral que Components/WaMe/WaMe.js (el componente ya establecido
      // en el resto de la app para esto — PaymentLinks.js, Prescription.js):
      // un número de 8 dígitos o menos es un local sin código de país (ej.
      // cargado a mano hace tiempo, antes de que se empezara a exigir el
      // formato completo) — wa.me lo rechaza o abre un chat roto. Sin este
      // chequeo, el botón "Enviar por WhatsApp" se mostraba igual, con
      // apariencia de funcionar, para un número que en la práctica no sirve.
      if (number.length <= 8) return null
      const professional = event.resource?.label?.split(',').map(part => part.trim()).filter(Boolean).reverse().join(' ') || 'tu profesional'
      // Bug real (UX), mismo patrón que #164/notify.js#buildHtml: el mensaje
      // pre-armado decía "te comparto la factura" para CUALQUIER PDF, incluida
      // una Nota de Crédito recién aprobada — a diferencia del email
      // automático (ya corregido), acá el profesional puede mandar el mensaje
      // SIN revisarlo (es un link pre-armado, un click y ya salió por
      // WhatsApp), llevando la misma confusión ("¿me están cobrando de
      // nuevo?") directo al paciente por otro canal.
      const text = isCreditNote
        ? t('arca_wa_message_nc', `Hola! Te comparto la Nota de Crédito que anula tu comprobante anterior (no es un cobro nuevo). La podés descargar acá: ${pdf}`, { pdf, professional })
        : t('arca_wa_message', `Hola! Te comparto la factura de tu consulta con ${professional}. La podés descargar acá: ${pdf}`, { pdf, professional })
      const params = new URLSearchParams({ phone: number, text, type: 'phone_number', app_absent: 0 })
      return `https://api.whatsapp.com/send/?${params}`
    } catch {
      return null
    }
  }

  const arcaDown = healthQuery.data?.up === false

  // ── Estado en la columna del turno ────────────────────────────────────────
  // event.invoice lo setea el worker server-side; cuando la última entrega fue
  // una NC trae creditNoteOf — eso significa "anulada", no "facturada".
  const marker = event.invoice || (approved && { status: 'approved', pdf: approved.pdf, creditNoteOf: activeNC ? approved.id : undefined })
  const annulled = !!(activeNC || marker?.creditNoteOf)
  // Hoisteado fuera del JSX a propósito: `invoice?.status === 'approved'`
  // usado directo como condición del conditional de más abajo (adyacente a
  // otro `{cond && <X/>}`) confunde a babel-eslint — marca hooks totalmente
  // ajenos, definidos antes en este mismo archivo, como "llamados
  // condicionalmente" (false positive, verificado con bisección: el mismo
  // JSX con `step === 'done'` en su lugar no dispara nada). No es una regla
  // general de "nunca dos conditionals adyacentes" — es específico de esta
  // combinación optional-chaining + comparación inline como condición.
  const invoiceApproved = invoice?.status === 'approved'

  return (
    <>
      {marker?.status === 'approved' && !annulled ? (
        <button className="btn btn-link btn-sm p-0 small text-success text-decoration-none" onClick={openModal}>
          {t('arca_invoiced', 'Facturado')} ✓
        </button>
      ) : annulled ? (
        <button className="btn btn-link btn-sm p-0 small text-muted text-decoration-none" onClick={openModal}>
          {t('arca_credit_note_emitted', 'Anulada (NC)')}
        </button>
      ) : inFlight ? (
        <button className="btn btn-link btn-sm p-0 small text-warning text-decoration-none" onClick={openModal}>
          {t('arca_invoicing', 'Facturando...')}
        </button>
      ) : (
        <button className="btn btn-link btn-sm p-0 small text-decoration-none" onClick={openModal}>
          {t('arca_invoice_action', 'Facturar')}
        </button>
      )}

      {open && (
        <div
          className="modal show d-flex align-items-center justify-content-center"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', minHeight: '100vh' }}
        >
          {/* Fix responsive (2026-08-14): 420px fijo sin tope superaba el
              viewport de cualquier celular — min() mantiene el ancho actual
              en desktop y cede en pantallas angostas. */}
          <div className="modal-dialog" role="document" style={{ minWidth: 'min(420px, calc(100vw - 32px))' }}>
            <div
              className="modal-content"
              ref={modalRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby="arca-invoice-title"
              tabIndex={-1}
            >
              <div className="modal-body p-4">
                <div className="d-flex justify-content-between mb-3">
                  <span className="d-flex align-items-center gap-2">
                    <h5 className="mb-0" id="arca-invoice-title">{t('arca_modal_title', 'Factura electrónica')}</h5>
                    {arcaEnabled && (
                      <>
                        <ArcaStatusIndicator />
                        <TrialInfoPopover
                          title={t('arca_title', 'Facturación ARCA')}
                          description={t('arca_trial_description', 'Emití comprobantes electrónicos habilitados por AFIP/ARCA directo desde DrApp — sin cambiar de sistema para facturar cada turno.')}
                          onLeaveReview={() => feedbackRef.current?.openManual()}
                        />
                      </>
                    )}
                  </span>
                  <button type="button" className="btn-close" aria-label={t('close', 'Cerrar')} onClick={closeModal} />
                </div>

                {/* Montado SIEMPRE (no solo si invoiceApproved) — sin esto,
                    clickear el badge ANTES de aprobar una factura no hace
                    nada (feedbackRef.current sigue en null, el componente ni
                    existe todavía). El disparador voluntario del badge tiene
                    que funcionar en cualquier momento, no solo después de
                    facturar — ese es justamente el punto de tenerlo ahí
                    (pedido de Nacho, 2026-08-10, segunda vuelta).
                    autoTriggerSignal SÍ sigue condicionado a invoiceApproved
                    (no a invoice?.id solo): invoice?.id ya existe desde el
                    202 inicial de la emisión (status 'queued'/'processing',
                    ver emit.js), antes de la aprobación real — sin este
                    gate, el auto-popup se dispararía prematuro, antes del
                    CAE. Bug real (reportado en uso real, 2026-08-10): antes
                    estaba atado a `step === 'done'`, que además de la
                    aprobación (CAE) esperaba al job 'deliver' (PDF,
                    asíncrono y posterior, ver el comentario de más arriba
                    sobre por qué el polling espera el pdf) — cerrar el modal
                    apenas se ve la aprobación (lo más natural) pasaba antes
                    de que el poll detectara el pdf, y el popup nunca llegaba
                    a montarse: ni siquiera se intentaba reclamar el cooldown
                    (0 filas en beta.feedback_prompts). invoice.status ya
                    está en 'approved' desde el primer poll que trae la
                    respuesta del backend (se setea antes del chequeo de
                    step, ver arriba), sin esperar el pdf. */}
                {/* featureName reusa la misma key que Arca.js/ArcaBilling.js
                    ("Facturación ARCA"), no el título local de este modal
                    ("Factura electrónica") — el feedback es sobre la
                    FEATURE arca como un todo, no sobre esta pantalla en
                    particular; un solo nombre reconocible en las 4
                    pantallas que la usan. */}
                <BetaFeedback ref={feedbackRef} featureKey="arca" featureName={t('arca_title', 'Facturación ARCA')} autoTriggerSignal={invoiceApproved ? invoice?.id : null} />

                {(identitiesQuery.isError || invoicesQuery.isError) && (
                  <div className="alert alert-danger small">
                    {/* No presuponer causa de conectividad (hallazgo de
                        copy, pasada 7 del loop): el fallo también puede ser
                        un 500 del backend o el team perdiendo el flag ARCA
                        a mitad de camino (ver arriba, not_found) — mismo
                        criterio neutral que los mensajes gemelos de
                        Arca.js/ArcaGrants.js. */}
                    {t('arca_modal_load_error', 'No pudimos cargar el estado de facturación.')}
                    <button
                      className="btn btn-sm btn-link p-0 ms-2"
                      onClick={() => { identitiesQuery.refetch(); invoicesQuery.refetch() }}
                    >
                      {t('arca_retry_load', 'Reintentar')}
                    </button>
                  </div>
                )}

                {arcaDown && (
                  <div className="alert alert-warning py-2 small">
                    {t('arca_down_warning', 'ARCA está con problemas en este momento — tu factura va a quedar en cola y se emitirá apenas se recupere.')}
                  </div>
                )}

                {/* Nivel superior, no adentro del bloque de "form de emisión" de
                    abajo (gateado por canEmitNew): "error" también lo usa la NC
                    (emitNC/mutateNC), que se dispara en la vista de "ya
                    facturado" — ahí canEmitNew es false, así que un error de la
                    NC quedaba seteado en el estado pero sin ningún lugar donde
                    mostrarse. */}
                {error && <div className="alert alert-danger py-2 small">{error}</div>}

                {/* Ya facturado: mostrar comprobante + acciones */}
                {(step === 'form' || step === 'failed' || step === 'queued-long') && approved && (
                  <div className="border rounded p-2 mb-3 small">
                    {/* Bug real (auditoría 2026-08-21): sin flex-wrap, el
                        número de factura + monto (y en desktop, sin espacio
                        de sobra dentro del modal ya angostado) podían
                        empujar los botones PDF/WhatsApp fuera de la fila en
                        vez de bajar de línea. */}
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                      <div>
                        <b>{t('arca_invoice', 'Factura')} {String(approved.ptoVta).padStart(5, '0')}-{String(approved.cbteNro).padStart(8, '0')}</b>
                        {' '}— $ {formatAmount(approved.impTotal)}
                        <div className="text-muted">CAE: {approved.cae}</div>
                      </div>
                      <div className="d-flex gap-2">
                        {approved.pdf && (
                          <a className="btn btn-sm btn-outline-primary" href={approved.pdf} target="_blank" rel="noopener noreferrer">PDF</a>
                        )}
                        {waLink(approved.pdf) && (
                          <a className="btn btn-sm btn-outline-success" href={waLink(approved.pdf)} target="whatsapp" rel="noopener noreferrer">
                            <IconWhatsApp width="14" height="14" />
                          </a>
                        )}
                      </div>
                    </div>
                    {activeNC ? (
                      <div className="text-muted mt-1">
                        {t('arca_nc_status', 'Nota de Crédito')}: {activeNC.status === 'approved' ? `CAE ${activeNC.cae}` : t('arca_nc_processing', 'en proceso')}
                        {activeNC.pdf && <> — <a href={activeNC.pdf} target="_blank" rel="noopener noreferrer">PDF</a></>}
                      </div>
                    ) : (
                      <button className="btn btn-sm btn-outline-danger mt-2" onClick={emitNC} disabled={ncBusy}>
                        {ncBusy ? t('arca_emitting_nc', 'Anulando...') : t('arca_emit_nc', 'Anular con Nota de Crédito')}
                      </button>
                    )}
                  </div>
                )}

                {/* Form de emisión (si no hay factura vigente — la anulada permite re-emitir corregida).
                    Gate por invoicesQuery: sin saber si YA hay factura, mostrar el form
                    invita a una doble emisión con flash confuso. */}
                {step === 'form' && !invoicesQuery.isLoading && !invoicesQuery.isError && !identitiesQuery.isError && canEmitNew && (
                  identitiesQuery.isLoading ? (
                    <div className="text-center my-3"><div className="spinner-border spinner-border-sm text-primary" /></div>
                  ) : identities.length === 0 ? (
                    <div className="alert alert-warning small">
                      {/* Reporte de Nacho (2026-08-21): el mensaje genérico no
                          decía QUÉ profesional era el que no tenía ARCA — en un
                          team multiprofesional, el usuario (que sí tiene ARCA)
                          asumía que el problema era suyo. El nombre se compone
                          FUERA de t() (el mock de i18n ignora interpolación). */}
                      <strong>{event.resource?.label || t('arca_the_professional', 'El profesional del turno')}</strong>
                      {' '}{t('arca_no_identity', 'no tiene una vinculación ARCA activa (propia o delegada) para la que puedas emitir.')}{' '}
                      {/* Bug real (mismo reporte): la URL omitía el segmento
                          `resources/` — /setup/<id>/payments no matchea ninguna
                          ruta y el link llevaba a cualquier lado. La card de
                          vinculación (Arca.js) vive en Setup → Resources →
                          Payments. */}
                      {/* El link SOLO si el usuario puede vincular en destino:
                          el propio profesional del turno, o staff (el caso
                          real de soporte vinculando por un profesional —
                          canLink en api/src/arca/permissions.js exime staff).
                          Para el resto el backend rechaza el POST — mandarlo
                          a una pantalla donde no puede hacer nada es peor que
                          decirle a quién pedírselo. La ruta del texto es la
                          NAVEGACIÓN visible (ítem "Profesionales" del menú
                          principal → perfil → Pagos), no la URL interna —
                          Setup no lista "Profesionales" en su propia barra. */}
                      {(user.state?.isStaff || user.team?.resourceID === resourceId) ? (
                        <Link to={window._l(`/setup/resources/${resourceId?.replace('resources/', '')}/payments`)} className="fw-bold">
                          {t('arca_go_setup', 'Ir a configuración')}
                        </Link>
                      ) : (
                        t('arca_ask_professional', 'Pedile que vincule su ARCA desde Profesionales → su perfil → Pagos.')
                      )}
                    </div>
                  ) : (
                    <>
                      {/* Card ARCA V2 (feedback de instancia): el aviso de
                          irreversibilidad vivía al final del form, después de
                          cargar todos los datos — el profesional lo veía
                          recién cuando ya había completado todo, demasiado
                          tarde para que pese en la decisión. Movido arriba de
                          "Factura a nombre de", lo primero que se ve al abrir
                          el form de emisión. */}
                      <div className="alert alert-light border py-2 small mb-3">
                        {t('arca_confirm_note', 'Una vez emitida, la factura no se puede editar ni borrar — solo se corrige con una Nota de Crédito.')}
                      </div>

                      {identities.length > 1 ? (
                        <div className="mb-2">
                          <label className="form-label small mb-1">{t('arca_identity', 'Facturar con el CUIT de')}</label>
                          <select className="form-select form-select-sm" value={identity?.fiscalResourceId} onChange={event_ => { formTouched.current = true; setIdentityId(event_.target.value) }}>
                            {identities.map(item => (
                              <option key={item.fiscalResourceId} value={item.fiscalResourceId}>
                                {item.own
                                  ? `${item.cuit} (${t('arca_own', 'propio')})`
                                  : `${item.label || item.cuit} — ${item.cuit} (${t('arca_delegated', 'delegado')})`}
                              </option>
                            ))}
                          </select>
                        </div>
                      ) : (
                        // Bug real (transparencia fiscal): con una sola identidad
                        // disponible el <select> de arriba nunca se mostraba — si
                        // esa única identidad era una delegada (no la propia, caso
                        // real de "clínica con CUIT compartido", §5.1.3), el
                        // profesional no tenía NINGÚN indicio en el form de que
                        // estaba a punto de emitir un comprobante fiscal real bajo
                        // el CUIT de otra persona, ni antes ni durante la carga.
                        identity && !identity.own && (
                          <div className="alert alert-warning py-1 px-2 small mb-2">
                            {t('arca_identity_delegated_notice', 'Vas a facturar con el CUIT delegado de {{label}} ({{cuit}}).', { label: identity.label || identity.cuit, cuit: identity.cuit })}
                          </div>
                        )
                      )}

                      <div className="mb-2">
                        <label className="form-label small mb-1">{t('arca_receptor', 'Factura a nombre de')}</label>
                        <input className="form-control form-control-sm" value={receptor.label}
                          onChange={event_ => { formTouched.current = true; setReceptor({ ...receptor, label: event_.target.value }) }} />
                        {suggestionSource === 'consumer_history' && (
                          <div className="form-text">{t('arca_suggestion_consumer', 'Precargado de la última factura de este paciente — revisalo igual.')}</div>
                        )}
                      </div>
                      <div className="row g-2 mb-2">
                        <div className="col-7">
                          <label className="form-label small mb-1">{t('arca_doc_tipo', 'Documento')}</label>
                          <select className="form-select form-select-sm" value={receptor.docTipo}
                            onChange={event_ => { formTouched.current = true; setReceptor({ ...receptor, docTipo: event_.target.value, docNro: Number(event_.target.value) === 99 ? '' : receptor.docNro }) }}>
                            {DOC_TIPOS.map(doc => <option key={doc.id} value={doc.id}>{doc.label}</option>)}
                          </select>
                        </div>
                        <div className="col-5">
                          <label className="form-label small mb-1">{t('arca_doc_nro', 'Número')}</label>
                          <input className="form-control form-control-sm" value={receptor.docNro}
                            disabled={Number(receptor.docTipo) === 99}
                            onChange={event_ => { formTouched.current = true; setReceptor({ ...receptor, docNro: event_.target.value }) }} />
                        </div>
                      </div>
                      <div className="mb-2">
                        <label className="form-label small mb-1">{t('arca_condicion_iva', 'Condición frente al IVA')}</label>
                        <select className="form-select form-select-sm" value={condicionIva} onChange={event_ => { formTouched.current = true; setCondicionIva(event_.target.value) }}>
                          {CONDICIONES_IVA.map(condicion => <option key={condicion.id} value={condicion.id}>{condicion.label}</option>)}
                          {/* El backend acepta más códigos que los que este selector expone
                              (tabla completa en fiscal.js/router.js) — la sugerencia (§6.10)
                              precarga lo que sea que haya quedado guardado en una factura o
                              config previa, que pudo haberse cargado con un código no listado
                              acá. Sin esta opción "fantasma", un <select> controlado con un
                              value sin <option> que lo matchee muestra el PRIMER ítem de la
                              lista como si estuviera seleccionado — el profesional vería
                              "Consumidor Final" en pantalla mientras en realidad se manda un
                              código distinto, sin darse cuenta. */}
                          {!CONDICIONES_IVA.some(condicion => condicion.id === Number(condicionIva)) && !Number.isNaN(Number(condicionIva)) && (
                            <option value={condicionIva}>{t('arca_condicion_iva_unlisted', 'Código {{id}} (cargado previamente)', { id: condicionIva })}</option>
                          )}
                        </select>
                        {/* Bug real (UX): el hint de "financier_history" estaba
                            ubicado bajo el input de RECEPTOR (arriba) pese a que
                            esa sugerencia (§6.10) nunca toca el receptor — solo
                            la condición IVA (suggest.js: `receptor: null` para
                            esta fuente). Un profesional leyendo el form de
                            arriba hacia abajo veía "Condición IVA precargada..."
                            debajo de "Factura a nombre de", antes incluso de
                            llegar al selector real que describe — movido acá,
                            donde corresponde. De paso se agrega el hint que
                            faltaba para la 3ª fuente ('config', el default
                            guardado por el propio profesional en su
                            vinculación) — las otras 2 fuentes SÍ avisan de
                            dónde salió el valor precargado, esta quedaba muda. */}
                        {suggestionSource === 'financier_history' && (
                          <div className="form-text">{t('arca_suggestion_financier', 'Condición IVA precargada de la última factura con este financiador.')}</div>
                        )}
                        {suggestionSource === 'config' && (
                          <div className="form-text">{t('arca_suggestion_config', 'Precargado de tu condición IVA por defecto — revisala si corresponde otra para este paciente.')}</div>
                        )}
                      </div>

                      {/* Responsable Inscripto (§6.11): la modalidad de cobro determina el
                          IVA discriminado — obligatoria, a diferencia de monotributista
                          (Factura C) que nunca discrimina. */}
                      {esResponsableInscripto && (
                        <div className="mb-2">
                          <label className="form-label small mb-1">{t('arca_modalidad_fiscal', 'Modalidad de cobro')}</label>
                          <select className="form-select form-select-sm" value={modalidadFiscal} onChange={event_ => { formTouched.current = true; setModalidadFiscal(event_.target.value) }}>
                            {MODALIDADES_FISCALES.map(modalidad => (
                              <option key={modalidad.id} value={modalidad.id}>{modalidad.label} — {modalidad.tratamiento}</option>
                            ))}
                          </select>
                          {/* alert-warning, no form-text (hallazgo de copy,
                              pasada 7 del loop): este aviso dice que el
                              cálculo de IVA que va a imprimirse en un
                              comprobante con CAE (irreversible) NO está
                              confirmado legalmente — mismo peso visual que
                              el aviso de identidad delegada de más arriba,
                              no el de un hint cosmético cualquiera. */}
                          <div className="alert alert-warning py-1 px-2 small mb-2">
                            {t('arca_modalidad_fiscal_hint', 'El tratamiento de IVA por modalidad es un esquema práctico, no confirmado todavía por consulta vinculante ante ARCA — consultalo con tu contador.')}
                          </div>
                          {(() => {
                            const parsedAmount = parseAmountInput(amount) || 0
                            const rate = IVA_RATE[modalidadFiscal]
                            if (!rate) {
                              return (
                                <div className="small mt-1">
                                  {t('arca_iva_exento', 'Exento')}: $ {formatAmount(parsedAmount)}
                                </div>
                              )
                            }
                            // Mismo cálculo en centavos enteros que fiscal.js#resolveIvaBreakdown
                            // (backend, la fuente real de lo que se factura) — redondear
                            // subtotal e IVA por separado en floats podía mostrar acá un
                            // preview que no sumaba exacto al total (o difería en 1 centavo
                            // del desglose real que termina en la factura/PDF).
                            const amountCents = Math.round(parsedAmount * 100)
                            const netoCents = Math.round(amountCents / (1 + rate))
                            const ivaCents = amountCents - netoCents
                            return (
                              <div className="small mt-1 text-muted">
                                {t('arca_iva_subtotal', 'Subtotal')}: $ {formatAmount(netoCents / 100)} + {t('arca_iva_label', 'IVA')}: $ {formatAmount(ivaCents / 100)}
                              </div>
                            )
                          })()}
                        </div>
                      )}
                      <div className="row g-2 mb-2">
                        <div className="col-6">
                          <label className="form-label small mb-1">{t('arca_serv_desde', 'Servicio — desde')}</label>
                          <input type="date" className="form-control form-control-sm" value={servDesde}
                            onChange={event_ => {
                              formTouched.current = true
                              setServDesde(event_.target.value)
                              applyServicePeriodToDescripcion(event_.target.value, servHasta)
                            }} />
                        </div>
                        <div className="col-6">
                          <label className="form-label small mb-1">{t('arca_serv_hasta', 'hasta')}</label>
                          <input type="date" className="form-control form-control-sm" value={servHasta}
                            onChange={event_ => {
                              formTouched.current = true
                              setServHasta(event_.target.value)
                              applyServicePeriodToDescripcion(servDesde, event_.target.value)
                            }} />
                        </div>
                        <div className="form-text">
                          {t('arca_serv_periodo_hint', 'Para facturar varias sesiones juntas (reintegro), extendé el rango — queda impreso en el comprobante.')}
                        </div>
                      </div>
                      <div className="mb-2">
                        <label className="form-label small mb-1">{t('arca_descripcion', 'Detalle (visible en la factura)')}</label>
                        <textarea
                          className="form-control form-control-sm"
                          rows="2"
                          maxLength="300"
                          value={descripcion}
                          onChange={event_ => { formTouched.current = true; descripcionTouched.current = true; setDescripcion(event_.target.value) }}
                        />
                        {descripcion.length > 240 && (
                          <div className="form-text text-end">{300 - descripcion.length} {t('arca_chars_left', 'caracteres restantes')}</div>
                        )}
                      </div>
                      <p className="small mb-2">
                        <button className="btn btn-link btn-sm p-0 text-decoration-none" onClick={() => setShowTips(!showTips)}>
                          {t('arca_tips_toggle', '💡 Tips para facturas de reintegro')}
                        </button>
                      </p>
                      {showTips && (
                        <div className="alert alert-light border py-2 small mb-2">
                          <ul className="mb-0 ps-3">
                            <li>{t('arca_tip_receptor', 'Emitila a nombre del afiliado titular de la prepaga/obra social (puede no ser el paciente, ej. el padre/madre).')}</li>
                            <li>{t('arca_tip_detalle', 'En el detalle indicá la prestación y la cantidad de sesiones — ej: "4 sesiones de psicoterapia individual". Las fechas van en el rango "Servicio desde/hasta" de arriba, no hace falta repetirlas acá.')}</li>
                            <li>{t('arca_tip_diagnostico', 'No incluyas diagnóstico ni motivo de consulta — no hace falta para el reintegro y protege la privacidad del paciente.')}</li>
                            <li>{t('arca_tip_plazo', 'Recordale al paciente presentarla pronto: la mayoría acepta facturas de hasta 180 días (algunas hasta 12 meses).')}</li>
                          </ul>
                        </div>
                      )}
                      <div className="mb-2" style={{ maxWidth: '180px' }}>
                        <label className="form-label small mb-1">{t('arca_amount', 'Monto')}</label>
                        <div className="input-group input-group-sm">
                          <span className="input-group-text">$</span>
                          <input
                            className="form-control"
                            inputMode="decimal"
                            value={amount}
                            onChange={event_ => { formTouched.current = true; setAmount(event_.target.value) }}
                          />
                        </div>
                        {/* Bug real: sin este chequeo, un turno SIN precio
                            propio (event.pricing?.total undefined — ahora
                            posible, ver guard de arriba) comparaba contra
                            Number(undefined) = NaN, y como cualquier número
                            !== NaN es siempre true, este aviso quedaba
                            SIEMPRE visible mostrando "$NaN" — ruido
                            permanente en el caso exacto (sin precio de
                            turno) que este fix vino a habilitar. */}
                        {event.pricing?.total != null && parseAmountInput(amount) !== Number(event.pricing.total) && (
                          <div className="form-text">
                            {t('arca_amount_differs', 'Distinto del monto del turno')} (${formatAmount(event.pricing.total)})
                          </div>
                        )}
                      </div>

                      <div className="d-flex justify-content-end gap-2">
                        <button className="btn btn-sm btn-secondary" onClick={closeModal}>{t('cancel', 'Cancelar')}</button>
                        <button className="btn btn-sm btn-primary" onClick={emit}>
                          {t('arca_emit_confirm', 'Emitir factura')}
                        </button>
                      </div>
                    </>
                  )
                )}

                {step === 'form' && invoicesQuery.isLoading && (
                  <div className="text-center my-3"><div className="spinner-border spinner-border-sm text-primary" /></div>
                )}

                {(step === 'polling' || step === 'polling-start' || (step === 'form' && inFlight && !approved)) && (
                  <div className="text-center my-3">
                    <div className="spinner-border spinner-border-sm text-primary me-2" />
                    <span className="small">{t('arca_emitting', 'Emitiendo comprobante en ARCA...')}</span>
                  </div>
                )}

                {step === 'queued-long' && (
                  <div className="alert alert-warning small">
                    {/* Bug real (introducido por el fix de arriba, "esperar
                        el PDF antes de done"): a este estado ahora también se
                        puede llegar con la factura YA aprobada (CAE real),
                        solo esperando el PDF (job 'deliver') — decir "la
                        emisión sigue en cola" ahí sería directamente falso,
                        el comprobante ya está aprobado. */}
                    {invoice?.status === 'approved'
                      ? t('arca_queued_long_pdf', 'El comprobante ya se aprobó — CAE {{cae}}. Estamos generando el PDF, puede demorar un poco más. No hace falta que esperes: se envía automáticamente en cuanto esté listo.', { cae: invoice.cae })
                      : t('arca_queued_long', 'La emisión sigue en cola — puede que ARCA esté con demoras. No hace falta que esperes: el comprobante se va a emitir y enviar automáticamente.')}
                  </div>
                )}

                {step === 'done' && invoice && (
                  <div className="alert alert-success small">
                    {/* Bug real (UX): decía "Comprobante emitido" para CUALQUIER
                        resultado, incluida una Nota de Crédito recién aprobada —
                        el profesional acaba de confirmar una acción irreversible
                        de "anular" (window.confirm en emitNC) y veía un mensaje
                        de éxito indistinguible del de facturar de cero, sin
                        ninguna confirmación explícita de que lo que se aprobó
                        fue efectivamente la anulación. Mismo dato (creditNoteOfId)
                        que ya distingue "Anulada" de "Aprobada" en el resto de
                        este mismo archivo (marker.creditNoteOf, activeNC). */}
                    <b>{invoice.creditNoteOfId ? t('arca_nc_emitted_ok', 'Nota de Crédito emitida') : t('arca_emitted_ok', 'Comprobante emitido')}</b> — CAE {invoice.cae}
                    <div className="mt-2 d-flex gap-2">
                      {invoice.pdf && <a className="btn btn-sm btn-outline-primary" href={invoice.pdf} target="_blank" rel="noopener noreferrer">PDF</a>}
                      {waLink(invoice.pdf, !!invoice.creditNoteOfId) && (
                        <a className="btn btn-sm btn-outline-success" href={waLink(invoice.pdf, !!invoice.creditNoteOfId)} target="whatsapp" rel="noopener noreferrer">
                          <IconWhatsApp width="14" height="14" className="me-1" />
                          {t('arca_send_wa', 'Enviar por WhatsApp')}
                        </a>
                      )}
                    </div>
                    {/* Bug real (UX/robustez): decía "ya se lo enviamos" con
                        certeza absoluta — pero notify.js#email atrapa el fallo
                        de cada envío con un simple `.catch(console.error)` (sin
                        alertar, mismo criterio que prescriptions/notify.js) —
                        si el envío real falla (dirección inválida, proveedor
                        caído), NADA se entera: ni el paciente, ni soporte, ni
                        el profesional, que se queda creyendo que ya salió. Para
                        un comprobante de reintegro con ventana de tiempo real
                        ante la prepaga (ver tips más abajo, "180 días"), esa
                        falsa certeza puede costarle al paciente el reintegro
                        entero. Reformulado para no afirmar con certeza algo que
                        no se puede garantizar, señalando el fallback que YA
                        existe (WhatsApp/PDF, arriba) en vez de solo confiar en
                        el envío automático.
                        Bug real #2 (pedido de Nacho, 2026-08-05): "si el
                        paciente tiene email cargado" era un hedge innecesario
                        — a diferencia de si LLEGÓ (eso sí es incierto, ver
                        arriba), si TIENE un email cargado es un dato que ya
                        tenemos (event.consumer, mismo origen que `phones`) —
                        mostrar la dirección real es más útil y más preciso
                        que dejarlo en condicional. */}
                    {/* Turnos de crontu (§negocio, 2026-08-05, pedido de
                        Nacho): el backend (deliver()) entrega la factura por
                        el canal propio de crontu, NO por el email directo de
                        DrApp — mostrar el mensaje de email acá sería
                        directamente falso para este caso. */}
                    <div className="text-muted mt-1">
                      {event.source === 'crontu'
                        ? t('arca_sent_via_crontu', 'Se envía al paciente por el sistema de turnos (crontu) — no por email directo de DrApp.')
                        : consumerEmail(event.consumer)
                          ? t('arca_email_sent', 'Se envía automáticamente a {{email}} — si no le llega, usá WhatsApp o el PDF de arriba.', { email: consumerEmail(event.consumer) })
                          : t('arca_no_email', 'El paciente no tiene ningún email cargado — compartí el comprobante por WhatsApp o descargá el PDF de arriba.')}
                    </div>
                    {/* El link de arriba (y el que se comparte por WhatsApp)
                        vence a los 7 días (tope duro de SigV4, ver
                        api/src/arca/upload.js — bug real corregido 2026-08-05,
                        antes decía 30 días, que ni siquiera se llegaba a
                        cumplir de verdad) — el email SÍ adjunta el PDF real,
                        así que es la vía que sigue funcionando después de esa
                        fecha. */}
                    <div className="text-muted mt-1">{t('arca_pdf_link_expires', 'El link al PDF vence en 7 días — el adjunto del email no.')}</div>
                  </div>
                )}

                {step === 'failed' && invoice && (
                  <div className="alert alert-danger small">
                    {/* Mismo hallazgo que el de arriba (step 'done'): una Nota de
                        Crédito rechazada mostraba el mismo texto genérico que una
                        factura rechazada — acá importa más todavía, porque el
                        botón de abajo dice "Corregir y reintentar" pero una NC no
                        tiene NADA que corregir (no es un form, es una reversión
                        fija) — confuso para el profesional que solo quiere
                        reintentar anular. */}
                    <b>{invoice.creditNoteOfId ? t('arca_nc_rejected', 'ARCA rechazó la Nota de Crédito') : t('arca_rejected', 'ARCA rechazó el comprobante')}</b>
                    {/* Antes solo traducía RECEPTOR_REQUERIDO_RG4444 (el hint de
                        abajo) — cualquier otro código clasificado (CUIT_NOT_DELEGATED,
                        PUNTO_VENTA_NO_RECE, RETRIES_EXHAUSTED) quedaba sin ninguna
                        explicación acá, justo el primer lugar donde el profesional ve
                        el rechazo. Mismo helper que ya usa el historial (ArcaInvoices.js). */}
                    {rejectionReason(invoice) && <div className="mt-1">{rejectionReason(invoice)}</div>}
                    {invoice.error?.code === 'RECEPTOR_REQUERIDO_RG4444' && (
                      <div className="mt-1">{t('arca_rg4444_hint', 'El monto supera el umbral que exige identificar al receptor — cargá el DNI del paciente y volvé a intentar.')}</div>
                    )}
                    <button className="btn btn-sm btn-outline-primary mt-2" onClick={() => { setStep('form'); setInvoice(null); invoicesQuery.refetch() }}>
                      {invoice.creditNoteOfId ? t('arca_nc_retry', 'Reintentar') : t('arca_retry', 'Corregir y reintentar')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default Facturar
